<template>

    <div class="practice-import mb-3 mt-page">

        <!-- action buttons -->
        <div class="float-right">
            <b-btn variant="default" class="mr-2"
                   @click="reset()">
                {{ $i18n.gt('LABEL_RESET') }}
            </b-btn>
            <b-btn variant="primary"
                   :disabled="!dataRows"
                   @click="upload()">
                {{ $i18n.gt('LABEL_IMPORT') }}
            </b-btn>
        </div>

        <h1>{{ $i18n.gt('HEADING_IMPORT_PRACTICES') }}</h1>

        <b-row class="justify-content-center">
            <b-col cols="6" col-offset="3">
                <b-alert show variant="warning" v-if="!companyId">
                    <p>This will import practices, and create a new company for each practice.</p>
                    <p>
                        To import practices into an existing company, use the link provided on
                        the Manage Practices page for the company (TBA).
                    </p>
                </b-alert>
                <b-form-file
                    v-model="file"
                    accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                    :state="Boolean(file)"
                    :placeholder="$i18n.gt('LABEL_SELECT_FILE')"
                    drop-placeholder="Drop file here...">
                </b-form-file>

                <div class="my-3"
                     v-if="file">
                    <b-form-checkbox v-model="hasHeaderRow">
                        {{ $i18n.gt('LABEL_FILE_HEADER_ROW') }}
                    </b-form-checkbox>
                </div>

            </b-col>
        </b-row>

        <b-row class="justify-content-center">
            <b-col cols="6" col-offset="3">
                <b-alert show variant="info" class="mt-3">
                    <div v-if="!dataRows">
                        <p>
                            {{ $i18n.gt('LABEL_SELECT_CSV') }}
                        </p>
                        <p>
                            {{ $i18n.gt('LABEL_CSV_DATA') }}:
                            <br/>
                            <b-btn variant="primary" size="sm" class="mt-2"
                                   href="/csv-templates/Wellcoaches practice import template.csv">
                                <shs-icon name="data-transfer-download"></shs-icon>
                                {{ $i18n.gt('LABEL_DOWNLOAD_CSV_TEMPLATE') }}
                            </b-btn>
                        </p>
                    </div>
                    <p v-else>
                        {{ $i18n.gt('LABEL_REVIEW_DATA_ERRORS') }}
                    </p>
                </b-alert>
            </b-col>
        </b-row>
        <b-row class="justify-content-center">
            <b-col cols="6" col-offset="3">
                <b-alert :show="hasInvalidData" variant="danger">
                    {{ $i18n.gt('LABEL_INVALID_DATA') }}
                </b-alert>
            </b-col>
        </b-row>

        <b-row class="justify-content-center">
            <b-col>
                <spinner-until :condition="!loading">
                    <table class="table table-striped my-3">
                        <thead>
                        <tr>
                            <th class="text-center"
                                :colspan="permittedColumns.length">
                                <h3>{{ $i18n.gt('HEADING_REQUIRED_COLUMNS') }}</h3>
                            </th>
                        </tr>
                        <tr>
                            <th v-for="(col, c) in permittedColumns" :key="c">
                                {{ col }}
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(row,r) in dataRows" :key="r">
                            <td v-for="(col, c) in permittedColumns" :key="c"
                                :class="getDataCellClass(r,c)">
                                {{ row[c] }}
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </spinner-until>
            </b-col>
        </b-row>

    </div>
</template>

<script>

import Vue from 'vue'
import Papa from 'papaparse'
import moment from 'moment'
import * as _ from 'lodash'

/**
 * Page to import practices from a CSV file
 */
export default Vue.extend({
    name: 'PracticeImport',
    props: {
        companyId: Number // if null, create a company for each practice - else attach practices to existing company
    },
    data() {
        return {
            file: null,
            hasHeaderRow: true,
            loading: false,
            // comment: 'Batch import ' + moment().format('M/D/YY'),
            dataRows: null,
            permittedColumns: [
                'practice.name',
                'practice.city',
                'practice.state',
                'practice.website',
                'practitioner.role',
                'practitioner.firstname',
                'practitioner.lastname',
                'practitioner.email',
                'diet_questionnaire',
            ],
            requiredColumns: [
                'practice.name',
                'practitioner.email',
            ],
            planOptions: [],
        }
    },
    computed: {
        hasInvalidData() {
            let has = false
            _.each(this.dataRows, (row, r) => {
                _.each(row, (col, c) => {
                    if (this.getDataCellClass(r, c) == 'error-cell') {
                        has = true
                    }
                })
            })
            return has
        }
    },
    methods: {
        parseFile() {
            this.loading = true
            let reader = new FileReader()
            reader.readAsText(this.file, 'UTF-8')
            reader.onload = (evt) => {
                Papa.parse(evt.target.result, {
                    worker: true,
                    complete: (results, file) => {
                        this.loading = false
                        this.dataRows = results.data
                    },
                    error: () => {
                        this.loading = false
                        alert('Error loading CSV file')
                    }
                })
            }
            reader.onerror = function () {
                this.loading = false
                alert('Error loading CSV file')
            }
        },
        getDataCellClass(row, col) {
            const colName = this.permittedColumns[col]
            const value = this.dataRows[row][col]
            if (this.hasHeaderRow && row == 0) {
                // header row is only valid if hasHeaderRow is checked
                return 'header-cell'
            } else if (!value && this.requiredColumns.indexOf(colName) != -1) {
                return 'missing-required-cell'
            } else {
                return ''
            }
        },
        upload() {
            this.$api.importPractices(this, this.dataRows, this.hasHeaderRow, this.companyId)
                .then((response) => {
                    this.$toasted.show('Practices imported', {duration: 3000})
                })
                .catch((e) => {
                    this.$utils.handleError(e, 'Error importing practices')
                })
        },
        reset() {
            this.dataRows = null
            this.file = null
        }
    },
    watch: {
        // watch for file selection
        file(newVal, oldVal) {
            if (newVal) {
                this.parseFile()
            }
        }
    }
})

</script>

<style lang="scss">
.practice-import {
    .header-cell {
        color: #ccc;
    }

    .error-cell {
        color: red;
    }
}
</style>
